import createCrudService from '@src/core/api/create-crud-service';
import { QUERY_KEYS } from '@src/shared/constants/query-keys';
import type { TBaseQuery } from '@src/shared/types/base-query';
import { toast } from 'react-hot-toast';
import { useMutation } from '@tanstack/react-query';

import { request } from '@core/api/request';
import { queryClient } from '@core/api/query-client';
import { objectToFormData } from '@src/shared/utils/helpers/convert-obj-to-form-data';
import { showToast } from '@src/shared/utils/helpers/show-toast';
import type { TPlacementHostCompany } from '@src/shared/types/placement-host-company';

export type THostCompanyQuery = {
  name?: string;
  status?: number;
} & TBaseQuery;

export type THostCompanyPayload = {
  country?: string;
  name?: string;
  logo?: string;
  description?: string;
  address_line_1?: string;
  address_line_2?: string;
  city?: string;
  state?: string;
  postal_code?: string;
  contact_name?: string;
  contact_job_title?: string;
  contact_email?: string;
  contact_phone_number?: string;
  website?: string;
};

type TUniversityBulkAssignLabelsBody = {
  ids: number[];
  labels: number[];
};

export const hostCompaniesApi = {
  ...createCrudService<
    TPlacementHostCompany,
    THostCompanyQuery,
    THostCompanyPayload
  >({
    queryKey: [QUERY_KEYS.getHostCompanies],
    url: 'placement-host-companies',
  }),

  useReorderUniversityStatuses: () => {
    return useMutation({
      mutationFn: async (ids: number[]) => {
        return await request({
          url: 'university-statuses/reorder/',
          method: 'POST',
          body: { ids },
        });
      },
      onError: (error: string) => {
        toast.error(error);
      },
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: [QUERY_KEYS.getUniversityStatuses],
        });
      },
    });
  },

  useImportUniversities: () => {
    return useMutation({
      mutationFn: async (file: File) => {
        return await request({
          url: 'universities/import-universities/',
          method: 'POST',
          body: objectToFormData({ file }),
        });
      },
      onError: (error: string) => {
        toast.error(error);
      },
      onSuccess: (res) => {
        res.detail &&
          showToast({
            title: 'Universities Import Started',
            description: res.detail,
            variant: 'success',
          });
        queryClient.invalidateQueries({
          queryKey: [QUERY_KEYS.getUniversities],
        });
      },
    });
  },

  useBulkAssignUniversitiesLabels: () =>
    useMutation({
      mutationFn: async (body: TUniversityBulkAssignLabelsBody) =>
        await request<void>({
          url: 'universities/bulk-assign-labels/',
          method: 'PATCH',
          body,
        }),
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: [QUERY_KEYS.getUniversities],
        });
        showToast({
          title: 'Bulk Assign Labels Success',
          description:
            'Labels have been assigned to the universities successfully',
          variant: 'success',
        });
      },
    }),
};
